/**
 * Canonical user roles, the effective-role resolver, and role-based capability
 * predicates.
 *
 * `authStore.user.role` holds the *effective* role — resolved once in
 * `fetchUser` via `resolveEffectiveRole` below, token-first. The raw DB value
 * is preserved alongside it as `authStore.user.accountRole`. Every predicate
 * here takes the effective role.
 */

export const ROLES = Object.freeze({
  ADMIN: 'admin',
  IMC: 'imc',
  CARRIER: 'carrier',
  DRIVER: 'driver',
})

/**
 * Resolves the role every permission check in the app keys off.
 *
 * Roles live in two independent stores that are updated separately — Kinde and
 * the `marketplace-users` DB (MV-2154: "we need to add the roles in kinde and
 * also in our DB, marketplace-users"). They drift. A driver who is also granted
 * the carrier role carries both roles in Kinde but persists `role: 'driver'` in
 * the DB, because the admin form writes `role: roles[0]`.
 *
 * The token wins whenever present, which is what legacy did — every carrier
 * check in `src/app/components/Permissions/Permissions.js` gated on
 * `client.tokenData.rol`. It is also the right value to key off:
 * `KindeTokenAdapter.mapKindeTokenToLegacyFormat` already skips `driver` /
 * `guest-driver` / `guest-carrier`, so the token reports the user's *privileged*
 * role. Reading the DB role instead stripped every carrier affordance from
 * dual-role users — no Assign Driver, no Accept/Reject Trip, no Remove Driver.
 *
 * Known trade-off: this demotes a user whose DB role outranks their token role
 * (DB `admin`, token `imc`). That is a Kinde data fix, not a code change —
 * `posthogIdentify` reports `role` and `accountRole` so the drift is visible.
 *
 * @param {Object} [params]
 * @param {string} [params.tokenRole] - `rol` from the Kinde token
 * @param {string} [params.accountRole] - `role` from the DB user record
 * @returns {string} Effective role, or 'guest' when neither source has one
 */
export const resolveEffectiveRole = ({ tokenRole, accountRole } = {}) =>
  tokenRole || accountRole || 'guest'

/**
 * Roles permitted to create, edit, or duplicate orders.
 *
 * This is an ALLOWLIST, mirroring legacy `src/app/models/Marketplace.js`:
 *
 *   ['imc', 'admin'].includes(rol) ? 'orders' : 'trips'
 *
 * Any role not listed here — carrier, driver, guest-*, unknown, undefined — is
 * denied. It must stay an allowlist: the React/MobX extraction (e296f397)
 * ported it as a `role === 'carrier'` denylist, which let drivers through to
 * the order editor in production.
 */
export const ORDER_MANAGER_ROLES = Object.freeze([ROLES.ADMIN, ROLES.IMC])

/**
 * Whether a role may create, edit, or duplicate orders.
 *
 * @param {string} [role] - Backend user role (`authStore.user.role`)
 * @returns {boolean}
 */
export const canManageOrders = (role) => ORDER_MANAGER_ROLES.includes(role)

/**
 * Roles the backend lets read a single order by uid.
 *
 * Mirrors `GET /marketplace/orders/:uid`, which is
 * `authorizeRoles(['admin', 'imc'])` in dnp-platform
 * (services/svc-marketplace/src/routers/orders.js). Same membership as
 * ORDER_MANAGER_ROLES today, but a separate constant on purpose: one is about
 * authoring orders, this one is about a read route, and they are free to drift.
 */
export const ORDER_READER_ROLES = Object.freeze([ROLES.ADMIN, ROLES.IMC])

/**
 * Roles the backend lets read a single trip by uid.
 *
 * Mirrors `GET /marketplace/trips/:uid` in dnp-platform
 * (services/svc-marketplace/src/routers/trips.js). Note that `imc` is NOT on
 * that list — an IMC user cannot read a trip directly. The guest roles have no
 * ROLES entry because nothing else in the app keys off them.
 */
export const TRIP_READER_ROLES = Object.freeze([
  ROLES.ADMIN,
  ROLES.CARRIER,
  ROLES.DRIVER,
  'guest-carrier',
  'guest-driver',
])

/**
 * Whether a role may read a single order by uid.
 *
 * @param {string} [role] - Effective user role (`authStore.user.role`)
 * @returns {boolean}
 */
export const canReadOrderByUid = (role) => ORDER_READER_ROLES.includes(role)

/**
 * Whether a role may read a single trip by uid.
 *
 * @param {string} [role] - Effective user role (`authStore.user.role`)
 * @returns {boolean}
 */
export const canReadTripByUid = (role) => TRIP_READER_ROLES.includes(role)
